import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  CheckCircle,
  CurrencyDollar,
  ShoppingBag,
  MagnifyingGlass,
  EnvelopeSimple,
  Phone,
  Calendar,
  WarningCircle,
  XCircle,
  ArrowRight,
  Plus,
} from '@phosphor-icons/react';
import { apiClient, useAuthStore } from '@dukene/shared';
import { format } from 'date-fns';

enum CustomerStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  BLOCKED = 'BLOCKED',
}

interface Customer {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  status: CustomerStatus;
  acceptsMarketing: boolean;
  totalSpent: number;
  ordersCount: number;
  createdAt: string;
  _count?: {
    orders: number;
  };
}

export default function Customers() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', email: '', phone: '' });
  const activeStoreId = useAuthStore((state) => state.activeStoreId);
  const queryClient = useQueryClient();

  const { data: response, error, isLoading } = useQuery<any>({
    queryKey: ['merchant-customers', activeStoreId, statusFilter, search],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter !== 'ALL') params.append('status', statusFilter);
      if (search) params.append('search', search);

      return apiClient.get<any>(`/customers/merchant?${params}`);
    },
    enabled: !!activeStoreId,
  });

  const createCustomerMutation = useMutation({
    mutationFn: async (data: typeof newCustomer) => {
      return apiClient.post('/customers', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchant-customers', activeStoreId] });
      setIsCreateModalOpen(false);
      setNewCustomer({ name: '', email: '', phone: '' });
    },
  });

  const customersList: Customer[] = response?.customers || (Array.isArray(response) ? response : []);

  const stats = {
    total: customersList.length,
    active: customersList.filter((c) => c.status === CustomerStatus.ACTIVE).length,
    totalRevenue: customersList.reduce((sum, c) => sum + Number(c.totalSpent || 0), 0),
    totalOrders: customersList.reduce((sum, c) => sum + (c._count?.orders || c.ordersCount || 0), 0),
  };

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-xl p-4 text-red-800 dark:text-red-300 flex items-center gap-2.5 text-xs font-semibold">
        <XCircle weight="fill" className="w-4 h-4 shrink-0" />
        <span>Failed to load store customer records. Please verify your connection or retry.</span>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* High-Density Polaris Admin Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm">
        <div>
          <h1 className="text-base font-semibold text-[#202223] dark:text-white">
            Customer Directory
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center justify-center gap-1.5 bg-[#2C6ECB] hover:bg-[#1f57a4] text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Customer
          </button>
        </div>
      </div>

      {/* KPI Metric Summary Tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 p-4 rounded-xl shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[11px] font-medium text-[#5C5F62] dark:text-slate-400">
              Total Customers
            </span>
            <p className="text-xl font-bold text-[#202223] dark:text-white font-mono mt-1">
              {stats.total.toLocaleString()}
            </p>
          </div>
          <div className="w-9 h-9 rounded-lg bg-[#F6F6F7] dark:bg-slate-800 flex items-center justify-center text-[#2C6ECB]">
            <Users weight="fill" className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 p-4 rounded-xl shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[11px] font-medium text-[#5C5F62] dark:text-slate-400">
              Active Buyers
            </span>
            <p className="text-xl font-bold text-[#202223] dark:text-white font-mono mt-1">
              {stats.active.toLocaleString()}
            </p>
          </div>
          <div className="w-9 h-9 rounded-lg bg-[#E3F1DF] text-[#006838] flex items-center justify-center">
            <CheckCircle weight="fill" className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 p-4 rounded-xl shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[11px] font-medium text-[#5C5F62] dark:text-slate-400">
              Customer Lifetime Revenue
            </span>
            <p className="text-xl font-bold text-[#202223] dark:text-white font-mono mt-1">
              KES {stats.totalRevenue.toLocaleString()}
            </p>
          </div>
          <div className="w-9 h-9 rounded-lg bg-[#F6F6F7] dark:bg-slate-800 flex items-center justify-center text-[#006838]">
            <CurrencyDollar weight="fill" className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 p-4 rounded-xl shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[11px] font-medium text-[#5C5F62] dark:text-slate-400">
              Total Orders Generated
            </span>
            <p className="text-xl font-bold text-[#202223] dark:text-white font-mono mt-1">
              {stats.totalOrders.toLocaleString()}
            </p>
          </div>
          <div className="w-9 h-9 rounded-lg bg-[#F6F6F7] dark:bg-slate-800 flex items-center justify-center text-[#E58A00]">
            <ShoppingBag weight="fill" className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Filter Toolbar & Data Table Container */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl shadow-sm overflow-hidden">
        {/* Search & Filter Bar */}
        <div className="p-3.5 border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/50 dark:bg-slate-900/40 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass
              weight="bold"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5F62]"
            />
            <input
              type="text"
              placeholder="Filter customers by name, email, or telephone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-8 pl-9 pr-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white placeholder-[#8C9196] focus:outline-none focus:ring-1 focus:ring-[#006838]"
            />
          </div>

          <div className="flex items-center gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-8 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-semibold text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
            >
              <option value="ALL">All Account Statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="BLOCKED">Blocked</option>
            </select>
          </div>
        </div>

        {/* Table Content */}
        {isLoading ? (
          <div className="p-12 space-y-3 animate-pulse">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-10 bg-[#F6F6F7] dark:bg-slate-900 rounded-lg" />
            ))}
          </div>
        ) : customersList.length === 0 ? (
          <div className="text-center py-16 px-6">
            <Users weight="fill" className="w-10 h-10 text-[#8C9196] mx-auto mb-3" />
            <h3 className="text-sm font-semibold text-[#202223] dark:text-white">
              No Customers Located
            </h3>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-1 max-w-sm mx-auto">
              {search || statusFilter !== 'ALL'
                ? 'Try clearing your search filters to display all customer records.'
                : 'Customer accounts are automatically generated when shoppers place an order on your store.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/60 dark:bg-slate-900/40 text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] dark:text-slate-400">
                  <th className="py-2.5 px-4">Customer Name</th>
                  <th className="py-2.5 px-4">Contact Details</th>
                  <th className="py-2.5 px-4">Account Status</th>
                  <th className="py-2.5 px-4 text-right">Orders</th>
                  <th className="py-2.5 px-4 text-right">Lifetime Spend</th>
                  <th className="py-2.5 px-4">Joined Date</th>
                  <th className="py-2.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E1E4E8] dark:divide-slate-800 text-xs">
                {customersList.map((customer: Customer) => {
                  const getStatusBadge = () => {
                    if (customer.status === CustomerStatus.ACTIVE) {
                      return (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-[#E3F1DF] text-[#006838] border border-[#B3DEC1]">
                          <CheckCircle weight="fill" className="w-3 h-3" />
                          Active
                        </span>
                      );
                    }
                    if (customer.status === CustomerStatus.BLOCKED) {
                      return (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200">
                          <XCircle weight="fill" className="w-3 h-3" />
                          Blocked
                        </span>
                      );
                    }
                    return (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                        <WarningCircle weight="fill" className="w-3 h-3" />
                        Inactive
                      </span>
                    );
                  };

                  const orderCount = customer._count?.orders || customer.ordersCount || 0;

                  return (
                    <tr
                      key={customer.id}
                      className="hover:bg-[#F6F6F7]/60 dark:hover:bg-slate-800/40 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-[#E3F1DF] text-[#006838] font-bold text-xs flex items-center justify-center shrink-0">
                            {(customer.firstName || 'C').charAt(0).toUpperCase()}
                            {(customer.lastName || '').charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <Link
                              to={`/customers/${customer.id}`}
                              className="font-semibold text-[#202223] dark:text-white hover:text-[#006838] transition-colors block truncate"
                            >
                              {customer.firstName} {customer.lastName}
                            </Link>
                            {customer.acceptsMarketing && (
                              <span className="inline-block text-[10px] text-[#006838] font-semibold">
                                Accepts Marketing
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="py-3 px-4">
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-1.5 text-[#5C5F62] dark:text-slate-300">
                            <EnvelopeSimple weight="bold" className="w-3.5 h-3.5 shrink-0" />
                            <span className="font-mono truncate max-w-[200px]">
                              {customer.email}
                            </span>
                          </div>
                          {customer.phone && (
                            <div className="flex items-center gap-1.5 text-[#5C5F62] dark:text-slate-400">
                              <Phone weight="bold" className="w-3.5 h-3.5 shrink-0" />
                              <span className="font-mono">{customer.phone}</span>
                            </div>
                          )}
                        </div>
                      </td>

                      <td className="py-3 px-4">{getStatusBadge()}</td>

                      <td className="py-3 px-4 text-right font-mono font-bold text-[#202223] dark:text-white">
                        {orderCount}
                      </td>

                      <td className="py-3 px-4 text-right font-mono font-bold text-[#006838]">
                        KES {Number(customer.totalSpent || 0).toLocaleString()}
                      </td>

                      <td className="py-3 px-4 text-[#5C5F62] dark:text-slate-400 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <Calendar weight="bold" className="w-3.5 h-3.5" />
                          <span>
                            {customer.createdAt
                              ? format(new Date(customer.createdAt), 'MMM d, yyyy')
                              : 'N/A'}
                          </span>
                        </div>
                      </td>

                      <td className="py-3 px-4 text-right">
                        <Link
                          to={`/customers/${customer.id}`}
                          className="inline-flex items-center gap-1 h-7 px-3 text-xs font-semibold rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-[#F6F6F7] transition-colors text-[#202223] dark:text-white"
                        >
                          View Profile
                          <ArrowRight weight="bold" className="w-3 h-3 text-[#5C5F62]" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Create Customer Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl w-full max-w-md p-5">
            <h2 className="text-base font-bold text-[#202223] dark:text-white mb-4">Add New Customer</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[#5C5F62] dark:text-slate-400 mb-1">Full Name *</label>
                <input
                  type="text"
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                  className="w-full h-8 px-3 text-xs border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-[#141618] text-[#202223] dark:text-white rounded-lg focus:outline-none focus:border-[#2C6ECB]"
                  placeholder="e.g. John Doe"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#5C5F62] dark:text-slate-400 mb-1">Email Address</label>
                <input
                  type="email"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                  className="w-full h-8 px-3 text-xs border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-[#141618] text-[#202223] dark:text-white rounded-lg focus:outline-none focus:border-[#2C6ECB]"
                  placeholder="e.g. john@example.com"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#5C5F62] dark:text-slate-400 mb-1">Phone Number</label>
                <input
                  type="tel"
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                  className="w-full h-8 px-3 text-xs border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-[#141618] text-[#202223] dark:text-white rounded-lg focus:outline-none focus:border-[#2C6ECB]"
                  placeholder="e.g. 0712345678"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="px-4 py-1.5 text-xs font-semibold text-[#5C5F62] hover:bg-[#F6F6F7] dark:text-slate-300 dark:hover:bg-slate-800 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => createCustomerMutation.mutate(newCustomer)}
                disabled={!newCustomer.name || createCustomerMutation.isPending}
                className="px-4 py-1.5 text-xs font-bold bg-[#2C6ECB] text-white rounded-lg hover:bg-[#1f57a4] disabled:opacity-50"
              >
                {createCustomerMutation.isPending ? 'Saving...' : 'Save Customer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
